import { AbsoluteFill, useCurrentFrame, interpolate } from 'remotion';

// Types
import type { EmployeeYearInReview } from '@/types';

// Theme
import { employeeYearInReviewTheme } from '@/components/EmployeeYearInReview/theme';

// Mocks
import { mockEmployeeYearInReview } from '@/mocks';

// Constants
import { SCENE_2_CONFIG, BACKGROUND_ZOOM, CONFETTI_ANIM } from './constants';

// Utils
import { calculateEngagement, shouldShowAnniversaryBadge } from '@/utils';

// Components
import { Circle } from '@/components/common/Circle';
import { Board } from './components/Board';
import { PolaroidStack } from './components/PolaroidStack';
import { Stats } from './components/Stats';
import { ProfileInfo } from './components/ProfileInfo';
import { ConfettiScene2 } from './components/Confetti';

/**
 * Scene 2: Employee profile showcase with board, polaroid, and stats.
 */
export const Scene2 = ({
  employee = mockEmployeeYearInReview.employee,
}: EmployeeYearInReview) => {
  const frame = useCurrentFrame();

  const {
    employee: { info: defaultInfo },
  } = mockEmployeeYearInReview;
  const {
    name = defaultInfo.name,
    title: role = defaultInfo.title,
    onboardedAt: onboardingDate = defaultInfo.onboardedAt,
    avatar: avatarUrl = defaultInfo.avatar,
  } = employee?.info ?? {};
  const { years } = calculateEngagement(onboardingDate);
  const showAnniversary = shouldShowAnniversaryBadge(years);
  const sceneDuration = showAnniversary
    ? SCENE_2_CONFIG.DURATION
    : SCENE_2_CONFIG.DURATION_WITHOUT_ANNIVERSARY;

  const backgroundScale = interpolate(
    frame,
    [0, sceneDuration],
    [BACKGROUND_ZOOM.SCALE_START, BACKGROUND_ZOOM.SCALE_END],
    {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    },
  );

  return (
    <AbsoluteFill className="overflow-hidden">
      {/* Background layer with zoom animation */}
      <AbsoluteFill
        className={employeeYearInReviewTheme.colors.bgPrimary}
        style={{
          transform: `scale(${backgroundScale})`,
          transformOrigin: 'center',
        }}
      >
        <Circle
          centerX={174}
          centerY={0}
          radius={195}
          opacity={0.25}
          className="bg-primary-600"
          name="Top Left Circle"
        />
        <Circle
          centerX={1564}
          centerY={999}
          radius={395}
          className={employeeYearInReviewTheme.colors.bgSlateAccent}
          name="Bottom Right Circle"
        />
      </AbsoluteFill>

      {/* Main Board with Text Content */}
      <Board showAnniversary={showAnniversary} sceneDuration={sceneDuration}>
        <ProfileInfo name={name} role={role} onboardingDate={onboardingDate} />
      </Board>

      {/* Polaroid Photo Stack */}
      <PolaroidStack avatarUrl={avatarUrl} />

      {/* Stats Blocks Group */}
      <Stats
        onboardingDate={onboardingDate}
        showAnniversary={showAnniversary}
      />

      {/* Confetti celebration effect */}
      {showAnniversary && <ConfettiScene2 startFrame={CONFETTI_ANIM.START} />}
    </AbsoluteFill>
  );
};
